import { Bookmark, Film, Image as ImageIcon, Loader2, MapPin, Package, Users } from "lucide-react"
import type { ReactNode } from "react"
import { useEffect, useMemo, useState } from "react"
import { toast } from "sonner"
import { ImageLightboxDialog } from "@/components/image-lightbox-dialog"
import { Button, Input } from "@/components/ui/primitives"
import { EntityThumbSquare } from "@/components/workspace/entity-thumb-square"
import { WorkspaceDialog } from "@/components/workspace/workspace-dialog"
import { useTranslation } from "@/hooks/use-translation"
import { cn } from "@/lib/utils"
import {
  createShotPreset,
  fetchShotPresetDraft,
  refreshShotPreset,
  type ShotPresetAssetMode,
  type ShotPresetDraft,
  type ShotPresetDraftAsset,
  type ShotPresetDraftEntity,
  type ShotPresetEntityMode,
  type ShotPresetGenerationRecipe,
} from "@/lib/shot-presets-api"

type WizardStep = "name" | "slots" | "assets" | "review"

type ShotPresetWizardProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: number
  shotId: number
  presetId?: number | null
  initialName?: string
  initialDescription?: string | null
  nested?: boolean | 2 | 3
  onSaved: (presetId: number) => void
}

function entityKindLabel(
  t: (key: "kindCharacter" | "kindLocation" | "kindProp") => string,
  kind: ShotPresetDraftEntity["kind"],
) {
  if (kind === "character") return t("kindCharacter")
  if (kind === "location") return t("kindLocation")
  return t("kindProp")
}

const STEPS: WizardStep[] = ["name", "slots", "assets", "review"]

function defaultModeForAsset(draft: ShotPresetDraft, asset: ShotPresetDraftAsset): ShotPresetAssetMode {
  const map =
    asset.type === "reference_input" ? draft.asset_defaults.reference_inputs : draft.asset_defaults.media_versions
  const mode = map?.[String(asset.id)]
  return mode === "omit" || mode === "hidden" || mode === "visible" ? mode : "omit"
}

export function ShotPresetWizard({
  open,
  onOpenChange,
  projectId,
  shotId,
  presetId,
  initialName,
  initialDescription,
  nested = true,
  onSaved,
}: ShotPresetWizardProps) {
  const { t } = useTranslation(["presets", "common"])
  const [step, setStep] = useState<WizardStep>("name")
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [draft, setDraft] = useState<ShotPresetDraft | null>(null)
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [entities, setEntities] = useState<ShotPresetDraftEntity[]>([])
  const [assetModes, setAssetModes] = useState<Record<string, ShotPresetAssetMode>>({})
  const [lightbox, setLightbox] = useState<{ url: string; title: string } | null>(null)

  useEffect(() => {
    if (!open) return
    setStep("name")
    setError(null)
    setBusy(false)
    setLoading(true)
    void fetchShotPresetDraft(projectId, shotId)
      .then((next) => {
        setDraft(next)
        setName((initialName ?? next.default_name).trim())
        setDescription(initialDescription ?? "")
        setEntities(next.entities)
        const modes: Record<string, ShotPresetAssetMode> = {}
        next.assets.forEach((asset) => {
          modes[`${asset.type}:${asset.id}`] = defaultModeForAsset(next, asset)
        })
        setAssetModes(modes)
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : t("loadFailed"))
        toast.error(e instanceof Error ? e.message : t("loadFailed"))
      })
      .finally(() => setLoading(false))
  }, [open, projectId, shotId, initialName, initialDescription, t])

  const stepLabel =
    step === "name"
      ? t("wizard.stepName")
      : step === "slots"
        ? t("wizard.stepSlots")
        : step === "assets"
          ? t("wizard.stepAssets")
          : t("wizard.stepReview")
  const stepIndex = STEPS.indexOf(step)
  const isEdit = presetId != null
  const title = isEdit ? t("wizard.editTitle") : t("wizard.title")

  const assetDecisions = useMemo(() => {
    const reference_inputs: Record<string, ShotPresetAssetMode> = {}
    const media_versions: Record<string, ShotPresetAssetMode> = {}
    Object.entries(assetModes).forEach(([key, mode]) => {
      const [type, id] = key.split(":")
      if (type === "reference_input") reference_inputs[id] = mode
      if (type === "media_version") media_versions[id] = mode
    })
    return { reference_inputs, media_versions }
  }, [assetModes])

  const close = () => onOpenChange(false)

  const handleSubmit = async () => {
    const trimmed = name.trim()
    if (!trimmed || !draft) return
    setBusy(true)
    setError(null)
    try {
      if (isEdit) {
        if (presetId == null) return
        const result = await refreshShotPreset(projectId, presetId, {
          name: trimmed,
          description: description.trim(),
          slot_configs: entities,
          asset_decisions: assetDecisions,
        })
        onSaved(result.preset_id)
      } else {
        const result = await createShotPreset(projectId, {
          shot_id: shotId,
          name: trimmed,
          description: description.trim(),
          slot_configs: entities,
          asset_decisions: assetDecisions,
        })
        onSaved(result.preset_id)
      }
      onOpenChange(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : t("saveFailed"))
    } finally {
      setBusy(false)
    }
  }

  const footer = (
    <>
      {stepIndex === 0 ? (
        <Button type="button" variant="secondary" size="sm" onClick={close} disabled={busy}>
          {t("common:cancel")}
        </Button>
      ) : (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => setStep(STEPS[stepIndex - 1])}
          disabled={busy}
        >
          {t("wizard.back")}
        </Button>
      )}
      {stepIndex < STEPS.length - 1 ? (
        <Button
          type="button"
          size="sm"
          onClick={() => setStep(STEPS[stepIndex + 1])}
          disabled={busy || loading || (step === "name" && !name.trim())}
        >
          {t("wizard.next")}
        </Button>
      ) : (
        <Button type="button" size="sm" onClick={() => void handleSubmit()} disabled={busy || loading || !name.trim()}>
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
          {isEdit ? t("wizard.save") : t("wizard.create")}
        </Button>
      )}
    </>
  )

  return (
    <>
      <WorkspaceDialog
        open={open}
        onOpenChange={onOpenChange}
        title={title}
        description={`${stepLabel} · ${stepIndex + 1}/${STEPS.length}`}
        closeAriaLabel={t("common:close")}
        size="lg"
        nested={nested}
        bodyClassName="space-y-4 p-5 pt-4"
        footer={footer}
      >
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-ui-link" />
          </div>
        ) : null}

        {!loading && step === "name" ? (
          <div className="space-y-3">
            <label className="block text-xs text-ui-text-secondary">
              {t("wizard.nameLabel")}
              <Input
                className="mt-1"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("wizard.namePlaceholder")}
                autoFocus
              />
            </label>
            <label className="block text-xs text-ui-text-secondary">
              {t("wizard.descriptionLabel")}
              <Input
                className="mt-1"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t("wizard.descriptionPlaceholder")}
              />
            </label>
          </div>
        ) : null}

        {!loading && step === "slots" ? (
          <div className="space-y-3">
            <p className="text-xs text-ui-text-muted">{t("wizard.slotsHint")}</p>
            {entities.length === 0 ? (
              <p className="text-sm text-ui-text-secondary">{t("wizard.noEntities")}</p>
            ) : (
              <ul className="space-y-2">
                {entities.map((entity, index) => (
                  <li
                    key={`${entity.kind}-${entity.source_character_id ?? entity.source_location_id ?? entity.source_prop_id}-${index}`}
                    className="flex items-center gap-3 rounded-lg border border-ui-border bg-ui-surface-sunken/40 p-2.5"
                  >
                    <EntityThumbSquare name={entity.label ?? entity.kind} imageUrl={entity.thumb_url} size="compact" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-ui-text-primary">{entity.label ?? entity.kind}</p>
                      <p className="text-[11px] text-ui-text-muted">{entityKindLabel(t, entity.kind)}</p>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      <div className="inline-flex overflow-hidden rounded-lg border border-ui-border-strong">
                        <ModeButton
                          active={entity.mode === "slot"}
                          label={t("wizard.modeSlot")}
                          onClick={() =>
                            setEntities((rows) =>
                              rows.map((row, i) =>
                                i === index ? { ...row, mode: "slot" as ShotPresetEntityMode } : row,
                              ),
                            )
                          }
                        />
                        <ModeButton
                          active={entity.mode === "internal"}
                          label={t("wizard.modeInternal")}
                          onClick={() =>
                            setEntities((rows) =>
                              rows.map((row, i) =>
                                i === index ? { ...row, mode: "internal" as ShotPresetEntityMode } : row,
                              ),
                            )
                          }
                        />
                      </div>
                      {entity.mode === "slot" ? (
                        <label className="flex items-center gap-1.5 text-[11px] text-ui-text-secondary">
                          <input
                            type="checkbox"
                            checked={entity.required}
                            onChange={(e) =>
                              setEntities((rows) =>
                                rows.map((row, i) => (i === index ? { ...row, required: e.target.checked } : row)),
                              )
                            }
                          />
                          {t("wizard.required")}
                        </label>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}

        {!loading && step === "assets" ? (
          <div className="space-y-3">
            <p className="text-xs text-ui-text-muted">{t("wizard.assetsHint")}</p>
            {!draft || draft.assets.length === 0 ? (
              <p className="text-sm text-ui-text-secondary">{t("wizard.noAssets")}</p>
            ) : (
              <ul className="space-y-2">
                {draft.assets.map((asset) => {
                  const key = `${asset.type}:${asset.id}`
                  const mode = assetModes[key] ?? "omit"
                  return (
                    <li
                      key={key}
                      className="flex items-center gap-3 rounded-lg border border-ui-border bg-ui-surface-sunken/40 p-2.5"
                    >
                      <button
                        type="button"
                        className="shrink-0"
                        onClick={() => {
                          if (asset.download_url) setLightbox({ url: asset.download_url, title: asset.label })
                        }}
                      >
                        <EntityThumbSquare name={asset.label} imageUrl={asset.thumb_url} size="compact" />
                      </button>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-ui-text-primary">{asset.label}</p>
                        <p className="text-[11px] text-ui-text-muted">
                          {asset.type === "reference_input" ? t("wizard.refKind") : t("wizard.versionKind")}
                          {asset.active ? ` · ${t("wizard.active")}` : ""}
                          {asset.character_name ? ` · ${asset.character_name}` : ""}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center">
                        {asset.mandatory ? (
                          <span className="text-[11px] font-medium text-ui-text-muted">{t("wizard.videoDemo")}</span>
                        ) : (
                          <div className="inline-flex overflow-hidden rounded-lg border border-ui-border-strong">
                            {(["omit", "hidden", "visible"] as const).map((value) => (
                              <ModeButton
                                key={value}
                                active={mode === value}
                                label={t(`wizard.${value}`)}
                                onClick={() => setAssetModes((current) => ({ ...current, [key]: value }))}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        ) : null}

        {!loading && step === "review" ? (
          <ReviewCard
            name={name.trim()}
            description={description.trim()}
            entities={entities}
            assets={draft?.assets ?? []}
            assetModes={assetModes}
            coverUrl={draft?.cover_url ?? null}
            recipe={draft?.generation_recipe}
          />
        ) : null}

        {error ? <div className="rounded-lg bg-rose-500/10 p-2 text-xs text-ui-status-failed">{error}</div> : null}
      </WorkspaceDialog>
      <ImageLightboxDialog
        open={lightbox != null}
        onOpenChange={(next) => {
          if (!next) setLightbox(null)
        }}
        url={lightbox?.url ?? null}
        title={lightbox?.title}
        nested={2}
      />
    </>
  )
}

function ModeButton({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "px-2 py-1 text-[11px] font-medium",
        active ? "bg-ui-accent text-white" : "bg-ui-surface text-ui-text-secondary hover:bg-ui-surface-raised",
      )}
    >
      {label}
    </button>
  )
}

function reviewKindIcon(kind: ShotPresetDraftEntity["kind"]) {
  if (kind === "character") return <Users className="h-3.5 w-3.5" />
  if (kind === "location") return <MapPin className="h-3.5 w-3.5" />
  return <Package className="h-3.5 w-3.5" />
}

function reviewAssetIcon(asset: ShotPresetDraftAsset) {
  if (asset.type === "reference_input") {
    if (asset.media === "video" || asset.media === "audio") return <Film className="h-3.5 w-3.5" />
    return <ImageIcon className="h-3.5 w-3.5" />
  }
  if (asset.kind === "video") return <Film className="h-3.5 w-3.5" />
  return <ImageIcon className="h-3.5 w-3.5" />
}

function ReviewCard({
  name,
  description,
  entities,
  assets,
  assetModes,
  coverUrl,
  recipe,
}: {
  name: string
  description: string
  entities: ShotPresetDraftEntity[]
  assets: ShotPresetDraftAsset[]
  assetModes: Record<string, ShotPresetAssetMode>
  coverUrl: string | null
  recipe?: ShotPresetGenerationRecipe
}) {
  const { t } = useTranslation(["presets"])
  const slotEntities = entities.filter((e) => e.mode === "slot")
  const internalEntities = entities.filter((e) => e.mode === "internal")
  const includedAssets = assets.filter((a) => {
    if (a.mandatory) return true
    const mode = assetModes[`${a.type}:${a.id}`] ?? "omit"
    return mode !== "omit"
  })
  const skippedAssets = assets.filter((a) => {
    if (a.mandatory) return false
    const mode = assetModes[`${a.type}:${a.id}`] ?? "omit"
    return mode === "omit"
  })

  const recipeRows: { label: string; value: string }[] = []
  if (recipe) {
    if (recipe.storyboard_image_provider) {
      recipeRows.push({ label: t("wizard.reviewProvider"), value: String(recipe.storyboard_image_provider) })
    }
    if (recipe.generation_model_overrides?.storyboard) {
      recipeRows.push({
        label: t("wizard.reviewStoryboardModel"),
        value: String(recipe.generation_model_overrides.storyboard),
      })
    }
    if (recipe.effective_video_generation_provider) {
      recipeRows.push({
        label: t("wizard.reviewVideoProvider"),
        value: String(recipe.effective_video_generation_provider),
      })
    }
    if (recipe.generation_model_overrides?.shot_video) {
      recipeRows.push({
        label: t("wizard.reviewVideoModel"),
        value: String(recipe.generation_model_overrides.shot_video),
      })
    }
    if (recipe.duration_target_sec != null) {
      recipeRows.push({
        label: t("wizard.reviewDuration"),
        value: t("wizard.reviewDurationValue", { sec: recipe.duration_target_sec }),
      })
    }
    if (recipe.frame_format) {
      recipeRows.push({ label: t("wizard.reviewFrameFormat"), value: String(recipe.frame_format) })
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-ui-text-muted">{t("wizard.reviewHint")}</p>
      <div className="rounded-lg border border-ui-border bg-ui-surface p-4">
        <div className="flex gap-3">
          <EntityThumbSquare
            name={name}
            imageUrl={coverUrl}
            size="overview"
            fallbackIcon={<Bookmark className="h-5 w-5 text-ui-text-muted" />}
          />
          <div className="min-w-0 flex-1">
            <p className="truncate text-base font-semibold text-ui-text-primary">
              {name || t("wizard.reviewUntitled")}
            </p>
            {description ? <p className="mt-0.5 line-clamp-2 text-xs text-ui-text-secondary">{description}</p> : null}
          </div>
        </div>
      </div>

      <ReviewSection title={t("wizard.reviewSlots", { count: slotEntities.length })}>
        {slotEntities.length === 0 ? (
          <p className="text-xs text-ui-text-muted">{t("wizard.reviewNoSlots")}</p>
        ) : (
          <ul className="space-y-1.5">
            {slotEntities.map((entity, index) => (
              <li key={`slot-${index}`} className="flex items-center gap-2 text-xs">
                <span className="text-ui-text-secondary">{reviewKindIcon(entity.kind)}</span>
                <span className="truncate text-ui-text-primary">{entity.label ?? entity.kind}</span>
                {entity.required ? (
                  <span className="ml-auto text-[10px] text-ui-text-muted">{t("wizard.required")}</span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </ReviewSection>

      {internalEntities.length > 0 ? (
        <ReviewSection title={t("wizard.reviewInternal", { count: internalEntities.length })}>
          <ul className="space-y-1.5">
            {internalEntities.map((entity, index) => (
              <li key={`int-${index}`} className="flex items-center gap-2 text-xs">
                <span className="text-ui-text-secondary">{reviewKindIcon(entity.kind)}</span>
                <span className="truncate text-ui-text-primary">{entity.label ?? entity.kind}</span>
              </li>
            ))}
          </ul>
        </ReviewSection>
      ) : null}

      <ReviewSection title={t("wizard.reviewIncludedAssets", { count: includedAssets.length })}>
        {includedAssets.length === 0 ? (
          <p className="text-xs text-ui-text-muted">{t("wizard.reviewNoAssets")}</p>
        ) : (
          <ul className="space-y-1.5">
            {includedAssets.map((asset) => {
              const key = `${asset.type}:${asset.id}`
              const mode = asset.mandatory ? "hidden" : (assetModes[key] ?? "omit")
              const visible = mode === "visible"
              return (
                <li key={key} className="flex items-center gap-2 text-xs">
                  <span className="text-ui-text-secondary">{reviewAssetIcon(asset)}</span>
                  <span className="truncate text-ui-text-primary">{asset.label}</span>
                  <span className="ml-auto text-[10px] text-ui-text-muted">
                    {asset.mandatory ? t("wizard.videoDemo") : visible ? t("wizard.visible") : t("wizard.hidden")}
                  </span>
                </li>
              )
            })}
          </ul>
        )}
      </ReviewSection>

      {skippedAssets.length > 0 ? (
        <ReviewSection title={t("wizard.reviewSkippedAssets", { count: skippedAssets.length })}>
          <ul className="space-y-1.5">
            {skippedAssets.map((asset) => {
              const key = `${asset.type}:${asset.id}`
              return (
                <li key={key} className="flex items-center gap-2 text-xs text-ui-text-muted">
                  <span>{reviewAssetIcon(asset)}</span>
                  <span className="truncate">{asset.label}</span>
                </li>
              )
            })}
          </ul>
        </ReviewSection>
      ) : null}

      {recipeRows.length > 0 ? (
        <ReviewSection title={t("wizard.reviewGeneration")}>
          <dl className="space-y-1.5">
            {recipeRows.map((row) => (
              <div key={row.label} className="flex items-center justify-between gap-2 text-xs">
                <dt className="text-ui-text-muted">{row.label}</dt>
                <dd className="truncate text-ui-text-primary">{row.value}</dd>
              </div>
            ))}
          </dl>
        </ReviewSection>
      ) : null}
    </div>
  )
}

function ReviewSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-2">
      <h3 className="text-[11px] font-semibold tracking-wide text-ui-text-muted uppercase">{title}</h3>
      {children}
    </section>
  )
}
